import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { whatsappTo } from '../../shared/whatsapp';
import { useMe } from '../session/SessionProvider';
import { useTabParam } from '../../shared/ui/useTabParam';

interface Vbs {
  id: string;
  name: string;
  startsOn: string;
  endsOn: string;
  kids: number;
  decisions: number;
}
interface Group {
  id: string;
  name: string;
  minAge: number | null;
  maxAge: number | null;
  teachers: string | null;
}
interface Kid {
  id: string;
  name: string;
  age: number | null;
  parentName: string | null;
  phone: string | null;
  notes: string | null;
  groupId: string | null;
  decisionOn: string | null;
  days: string[];
}
type Detail = Vbs & { groups: Group[]; kidsList: Kid[]; canManage: boolean };

const dayLabel = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('es', { weekday: 'short', day: 'numeric', month: 'short' });
function daysOf(v: Vbs): string[] {
  const out: string[] = [];
  const d = new Date(`${v.startsOn}T12:00:00`);
  const end = new Date(`${v.endsOn}T12:00:00`);
  while (d <= end) {
    out.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/** Asambleas de Dios · Escuela Bíblica de Vacaciones. */
export function AdVbsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/multichurch/churches/${churchId}/ad/vbs`;
  const list = useAsync((signal) => (churchId ? api.get<{ items: Vbs[]; canManage: boolean }>(base, signal) : Promise.resolve({ items: [], canManage: false })), [base]);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!churchId) return <div className="page"><EmptyState title="Elige primero una iglesia en el selector de arriba." /></div>;
  const items = list.status === 'ready' ? list.data.items : [];
  // Mismo criterio que el servidor: la administración y los líderes de Niños y de la Escuela Dominical.
  const canManage = list.status === 'ready' && list.data.canManage;
  const current = selected ?? items[0]?.id ?? null;

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setError(null);
    try {
      const r = await api.post<{ id: string }>(base, { name: String(f.get('name') ?? '').trim(), startsOn: String(f.get('startsOn')), endsOn: String(f.get('endsOn')) });
      setSelected(r.id);
      setCreating(false);
      list.reload();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <div className="page">
      <header className="page__header page__header--actions">
        <div>
          <p className="eyebrow">Asambleas de Dios</p>
          <h1>Escuela Bíblica de Vacaciones</h1>
          <p className="muted">Inscribe a los niños (de la iglesia o invitados), acomódalos por edad, pasa lista cada día y registra quién aceptó a Cristo.</p>
        </div>
        {canManage && !creating ? <Button onClick={() => setCreating(true)}>Nueva EBV</Button> : null}
      </header>
      {error ? <Alert>{error}</Alert> : null}
      {creating ? (
        <Card title="Nueva EBV">
          <form className="form" onSubmit={create}>
            <Field label="Nombre" name="name" required minLength={2} maxLength={120} placeholder={`EBV ${new Date().getFullYear()}`} />
            <div className="form__row">
              <Field label="Empieza" name="startsOn" type="date" required />
              <Field label="Termina" name="endsOn" type="date" required />
            </div>
            <div className="actions actions--wrap">
              <Button type="submit">Crear EBV</Button>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
      {list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {items.length > 1 ? (
        <div className="field">
          <label htmlFor="vbs-select">EBV</label>
          <select id="vbs-select" value={current ?? ''} onChange={(e) => setSelected(e.target.value)}>
            {items.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {current ? <VbsDetail key={current} base={`${base}/${current}`} onChanged={list.reload} /> : null}
      {list.status === 'ready' && items.length === 0 && !creating ? (
        <EmptyState title={canManage ? 'Todavía no hay EBV. Crea la primera con «Nueva EBV».' : 'Todavía no hay EBV registradas.'} />
      ) : null}
    </div>
  );
}

function VbsDetail({ base, onChanged }: { base: string; onChanged: () => void }) {
  const data = useAsync((signal) => api.get<Detail>(base, signal), [base]);
  const [tab, setTab] = useTabParam(['kids', 'day', 'groups', 'summary'] as const);
  const [day, setDay] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // Al recargar se sigue mostrando lo último (pasar lista no parpadea).
  const [last, setLast] = useState<Detail | null>(null);
  useEffect(() => {
    if (data.status === 'ready') setLast(data.data);
  }, [data]);
  const v = data.status === 'ready' ? data.data : last;
  if (!v) return data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : <Loading />;
  const days = daysOf(v);
  const today = new Date().toISOString().slice(0, 10);
  const currentDay = day ?? (days.includes(today) ? today : days[0]!);
  const groupName = (id: string | null) => v.groups.find((g) => g.id === id)?.name ?? 'Sin grupo';

  async function run(action: () => Promise<unknown>) {
    setMsg(null);
    try {
      await action();
      data.reload();
      onChanged();
    } catch (e) {
      setMsg(errorMessage(e));
    }
  }

  function addKid(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    const body = { name: t('name'), age: t('age') ? Number(t('age')) : null, parentName: t('parentName'), phone: t('phone'), notes: t('notes'), groupId: t('groupId') };
    e.currentTarget.reset();
    void run(() => api.post(`${base}/kids`, body));
  }

  function addGroup(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const n = (k: string) => (String(f.get(k) ?? '').trim() ? Number(f.get(k)) : null);
    const body = { name: String(f.get('name') ?? '').trim(), minAge: n('minAge'), maxAge: n('maxAge'), teachers: String(f.get('teachers') ?? '').trim() || null };
    e.currentTarget.reset();
    void run(() => api.post(`${base}/groups`, body));
  }

  const sections = [...v.groups.map((g) => g.id as string | null), null];
  const info = (
    <Card title={v.name}>
      <p className="small muted">
        {dayLabel(v.startsOn)} al {dayLabel(v.endsOn)} · {v.kids} {v.kids === 1 ? 'niño inscrito' : 'niños inscritos'} · {v.decisions}{' '}
        {v.decisions === 1 ? 'aceptó a Cristo' : 'aceptaron a Cristo'}
      </p>
    </Card>
  );
  const groupLine = (g: Group) => [g.minAge !== null || g.maxAge !== null ? `${g.minAge ?? '…'} a ${g.maxAge ?? '…'} años` : null, g.teachers].filter(Boolean).join(' · ');
  if (!v.canManage)
    return (
      <>
        {info}
        <Card title="Grupos por edad">
          {v.groups.length === 0 ? <p className="small muted">Todavía no hay grupos.</p> : null}
          <ul className="list">
            {v.groups.map((g) => (
              <li key={g.id} className="list__item">
                <span>
                  <strong>{g.name}</strong> <span className="small muted">{groupLine(g)}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="small muted">La inscripción y la lista de cada día las llevan la administración y los líderes de Niños y de la Escuela Dominical.</p>
        </Card>
      </>
    );
  return (
    <>
      {info}
      {msg ? <Alert>{msg}</Alert> : null}
      <div className="tabs tabs--scroll" role="tablist" aria-label="EBV">
        {(
          [
            ['kids', 'Inscritos'],
            ['day', 'Pasar lista'],
            ['groups', 'Grupos'],
            ['summary', 'Resumen'],
          ] as const
        ).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={`tabs__tab${tab === k ? ' tabs__tab--active' : ''}`} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'kids' ? (
        <>
          <Card title="Inscribir">
            <form className="form" onSubmit={addKid}>
              <div className="form__row">
                <Field label="Nombre del niño o niña" name="name" required minLength={2} maxLength={120} />
                <Field label="Edad" name="age" type="number" min={0} max={18} />
              </div>
              <div className="form__row">
                <Field label="Papá, mamá o tutor" name="parentName" maxLength={120} />
                <Field label="Teléfono" name="phone" type="tel" maxLength={30} />
              </div>
              <div className="form__row">
                <Field label="Alergias o notas" name="notes" maxLength={500} />
                <div className="field">
                  <label htmlFor="kid-group">Grupo</label>
                  <select id="kid-group" name="groupId" defaultValue="">
                    <option value="">Según su edad</option>
                    {v.groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <Button type="submit">Inscribir</Button>
            </form>
          </Card>
          {sections.map((gid) => {
            const kids = v.kidsList.filter((k) => k.groupId === gid);
            if (kids.length === 0) return null;
            return (
              <Card key={gid ?? 'none'} title={`${groupName(gid)} (${kids.length})`}>
                <ul className="list">
                  {kids.map((k) => (
                    <li key={k.id} className="list__item list__item--actions">
                      <span>
                        <strong>{k.name}</strong>
                        <span className="small muted">
                          {' '}
                          {[k.age !== null ? `${k.age} años` : null, k.parentName, k.notes].filter(Boolean).join(' · ')}
                        </span>
                        {k.phone ? (
                          <span className="small">
                            {' '}
                            · <a href={`tel:${k.phone.replace(/[^\d+]/g, '')}`}>{k.phone}</a> ·{' '}
                            <a href={whatsappTo(k.phone)} target="_blank" rel="noopener noreferrer">
                              WhatsApp
                            </a>
                          </span>
                        ) : null}
                      </span>
                      <Button variant="ghost" onClick={() => window.confirm(`¿Quitar a ${k.name}?`) && void run(() => api.delete(`${base}/kids/${k.id}`))}>
                        Quitar
                      </Button>
                    </li>
                  ))}
                </ul>
              </Card>
            );
          })}
          {v.kidsList.length === 0 ? <EmptyState title="Todavía no hay niños inscritos." /> : null}
        </>
      ) : null}

      {tab === 'day' ? (
        <Card title="Pasar lista">
          <div className="actions actions--wrap" role="group" aria-label="Día">
            {days.map((d) => (
              <button key={d} type="button" className={`chip ${d === currentDay ? 'chip--active' : ''}`} aria-pressed={d === currentDay} onClick={() => setDay(d)}>
                {dayLabel(d)}
              </button>
            ))}
          </div>
          <p className="small muted">
            {v.kidsList.filter((k) => k.days.includes(currentDay)).length} de {v.kidsList.length} presentes.
          </p>
          <ul className="list">
            {v.kidsList.map((k) => (
              <li key={k.id} className="list__item list__item--actions">
                <label className="check">
                  <input
                    type="checkbox"
                    checked={k.days.includes(currentDay)}
                    onChange={(e) => void run(() => api.post(`${base}/kids/${k.id}/mark`, { day: currentDay, present: e.target.checked }))}
                  />
                  <span>
                    {k.name} <span className="small muted">· {groupName(k.groupId)}</span>
                  </span>
                </label>
                <label className="check small">
                  <input
                    type="checkbox"
                    checked={k.decisionOn !== null}
                    onChange={(e) => void run(() => api.post(`${base}/kids/${k.id}/mark`, { decisionOn: e.target.checked ? currentDay : null }))}
                  />
                  Aceptó a Cristo
                </label>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {tab === 'groups' ? (
        <Card title="Grupos por edad">
          <ul className="list">
            {v.groups.map((g) => (
              <li key={g.id} className="list__item list__item--actions">
                <span>
                  <strong>{g.name}</strong>{' '}
                  <span className="small muted">{groupLine(g)}</span>
                </span>
                <Button variant="ghost" onClick={() => window.confirm(`¿Quitar el grupo ${g.name}?`) && void run(() => api.delete(`${base}/groups/${g.id}`))}>
                  Quitar
                </Button>
              </li>
            ))}
          </ul>
          <form className="form" onSubmit={addGroup}>
            <Field label="Nombre del grupo" name="name" required minLength={2} maxLength={80} />
            <div className="form__row">
              <Field label="Edad desde" name="minAge" type="number" min={0} max={18} />
              <Field label="Edad hasta" name="maxAge" type="number" min={0} max={18} />
            </div>
            <Field label="Maestros" name="teachers" maxLength={300} />
            <Button type="submit" variant="secondary">
              Agregar grupo
            </Button>
          </form>
        </Card>
      ) : null}

      {tab === 'summary' ? (
        <Card title="Resumen">
          <ul className="ad-tiles">
            <li>
              <strong>{v.kids}</strong>
              <span>Inscritos</span>
            </li>
            <li>
              <strong>{v.decisions}</strong>
              <span>Aceptaron a Cristo</span>
            </li>
            <li>
              <strong>{v.kidsList.filter((k) => k.days.length === 0).length}</strong>
              <span>Nunca vinieron</span>
            </li>
          </ul>
          <h3>Asistencia por día</h3>
          <ul className="list">
            {days.map((d) => (
              <li key={d} className="list__item list__item--actions">
                <span>{dayLabel(d)}</span>
                <strong>{v.kidsList.filter((k) => k.days.includes(d)).length}</strong>
              </li>
            ))}
          </ul>
          {v.decisions ? (
            <>
              <h3>Aceptaron a Cristo</h3>
              <ul className="ad-names">
                {v.kidsList
                  .filter((k) => k.decisionOn)
                  .map((k) => (
                    <li key={k.id}>
                      {k.name}
                      {k.parentName ? <span className="small muted"> · {k.parentName}</span> : null}
                      {k.phone ? <span className="small muted"> · {k.phone}</span> : null}
                    </li>
                  ))}
              </ul>
              <p className="small muted">Da seguimiento a estas familias: son una gran oportunidad para invitarlas a la iglesia.</p>
            </>
          ) : null}
        </Card>
      ) : null}
    </>
  );
}
